import { useEffect, useMemo, useRef, useState } from "react";
import { Link2, Pencil, TriangleAlert, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  createEntityRelationshipDraft,
  entityRelationshipLabelKeys,
  entityRelationshipLabels,
  entityRelationshipTargetKindLabelKeys,
  formatEntityErrorMessage,
  type CreateEntityRelationshipInput,
  type EntityCardView,
  type EntityRelationshipDraft,
  type EntityRelationshipTargetKind,
  type EntityRelationshipTargetOption,
  type EntityRelationshipView,
  type UpdateEntityRelationshipInput,
} from "@/features/entities";

type EntityRelationshipPanelProps = {
  card: EntityCardView;
  onCreateRelationship: (
    input: Omit<CreateEntityRelationshipInput, "projectId" | "sourceEntityCardId">,
  ) => Promise<EntityRelationshipView>;
  onDeleteRelationship: (
    relationshipId: string,
  ) => Promise<{ deletedRelationshipId: string }>;
  onSaveRelationship: (
    relationshipId: string,
    input: Omit<
      UpdateEntityRelationshipInput,
      "projectId" | "relationshipId" | "sourceEntityCardId"
    >,
  ) => Promise<EntityRelationshipView>;
  targetOptions: EntityRelationshipTargetOption[];
};

const BROKEN_PREFIX = "__broken__:";

function getTargetKindLabel(
  kind: EntityRelationshipTargetKind,
  t: (key: string) => string,
) {
  return t(entityRelationshipTargetKindLabelKeys[kind]);
}

function getRelationshipLabelText(
  relationship: EntityRelationshipView,
  t: (key: string) => string,
) {
  if (relationship.label === "custom") {
    return relationship.customLabel?.trim() || relationship.labelText;
  }

  return t(entityRelationshipLabelKeys[relationship.label]);
}

function toTargetValue(
  targetId: string,
  availableIds: Set<string>,
) {
  return availableIds.has(targetId) ? targetId : `${BROKEN_PREFIX}${targetId}`;
}

export function EntityRelationshipPanel({
  card,
  onCreateRelationship,
  onDeleteRelationship,
  onSaveRelationship,
  targetOptions,
}: EntityRelationshipPanelProps) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<EntityRelationshipDraft>(() =>
    createEntityRelationshipDraft(),
  );
  const [editingRelationshipId, setEditingRelationshipId] = useState<string | null>(null);
  const [panelError, setPanelError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [showComposer, setShowComposer] = useState(false);
  const labelTriggerRef = useRef<HTMLButtonElement | null>(null);
  const customLabelRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    setDraft(createEntityRelationshipDraft());
    setEditingRelationshipId(null);
    setPanelError(null);
    setSaving(false);
    setShowComposer(false);
  }, [card.id]);

  useEffect(() => {
    if (!showComposer) {
      return;
    }

    const nextFocusTarget =
      draft.label === "custom" ? customLabelRef.current : labelTriggerRef.current;
    const handle = window.requestAnimationFrame(() => {
      nextFocusTarget?.focus();
    });

    return () => window.cancelAnimationFrame(handle);
  }, [draft.label, showComposer]);

  const entityTargetOptions = useMemo(
    () => targetOptions.filter((option) => option.kind === "entity-card"),
    [targetOptions],
  );
  const loreTargetOptions = useMemo(
    () => targetOptions.filter((option) => option.kind === "lore-entry"),
    [targetOptions],
  );
  const currentTargetOptions =
    draft.targetKind === "entity-card" ? entityTargetOptions : loreTargetOptions;
  const currentTargetIds = new Set(currentTargetOptions.map((option) => option.id));

  function resetComposer() {
    setDraft(createEntityRelationshipDraft());
    setEditingRelationshipId(null);
    setPanelError(null);
    setSaving(false);
    setShowComposer(false);
  }

  function startCreateRelationship() {
    setDraft(createEntityRelationshipDraft());
    setEditingRelationshipId(null);
    setPanelError(null);
    setShowComposer(true);
  }

  function startEditRelationship(relationship: EntityRelationshipView) {
    setDraft({
      customLabel: relationship.customLabel ?? "",
      label: relationship.label,
      targetId: relationship.targetId,
      targetKind: relationship.targetKind,
    });
    setEditingRelationshipId(relationship.id);
    setPanelError(null);
    setShowComposer(true);
  }

  async function handleSaveRelationship() {
    setSaving(true);
    setPanelError(null);

    try {
      if (editingRelationshipId) {
        await onSaveRelationship(editingRelationshipId, draft);
      } else {
        await onCreateRelationship(draft);
      }
      resetComposer();
    } catch (error) {
      setSaving(false);
      setPanelError(t(formatEntityErrorMessage(error)));
    }
  }

  async function handleDeleteRelationship(
    relationshipId: string,
    isActiveEditor: boolean,
  ) {
    try {
      await onDeleteRelationship(relationshipId);
      if (isActiveEditor) {
        resetComposer();
      }
    } catch (error) {
      setPanelError(t(formatEntityErrorMessage(error)));
    }
  }

  const hasBrokenTarget =
    Boolean(draft.targetId) && !currentTargetIds.has(draft.targetId);
  const targetValue = draft.targetId
    ? toTargetValue(draft.targetId, currentTargetIds)
    : "";
  const canSave =
    !saving &&
    draft.targetId.length > 0 &&
    (draft.label !== "custom" || draft.customLabel.trim().length > 0);
  const targetKindLabel = getTargetKindLabel(draft.targetKind, t);

  return (
    <section className="space-y-4 rounded-[30px] border border-white/70 bg-white/80 p-5 shadow-[0_22px_70px_-34px_rgba(40,31,19,0.44)]">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <p className="text-sm font-semibold text-foreground">
            {t("entities.relationships.title")}
          </p>
          <p className="text-sm text-muted-foreground">
            {t("entities.relationships.description")}
          </p>
        </div>
        {!showComposer ? (
          <Button onClick={startCreateRelationship} type="button" variant="outline">
            <Link2 className="h-4 w-4" />
            {t("entities.relationships.add")}
          </Button>
        ) : null}
      </div>

      {showComposer ? (
        <div className="space-y-4 rounded-[24px] border border-border/70 bg-white/75 p-4">
          <div className="space-y-1">
            <p className="text-sm font-semibold text-foreground">
              {editingRelationshipId
                ? t("entities.relationships.editTitle")
                : t("entities.relationships.newTitle")}
            </p>
            <p className="text-xs text-muted-foreground">
              {t("entities.relationships.composerDescription")}
            </p>
          </div>

          {panelError ? (
            <p className="rounded-2xl border border-destructive/20 bg-destructive/10 px-4 py-3 text-sm text-destructive">
              {panelError}
            </p>
          ) : null}

          <div className="grid gap-4">
            <div className="space-y-2">
              <Label htmlFor="entity-relationship-label">
                {t("entities.relationships.fields.label")}
              </Label>
              <Select
                onValueChange={(value) =>
                  setDraft((current) => ({
                    ...current,
                    customLabel: value === "custom" ? current.customLabel : "",
                    label: value as (typeof entityRelationshipLabels)[number],
                  }))
                }
                value={draft.label}
              >
                <SelectTrigger
                  aria-label={t("entities.relationships.fields.label")}
                  id="entity-relationship-label"
                  ref={labelTriggerRef}
                >
                  <SelectValue placeholder={t("entities.relationships.labelPlaceholder")} />
                </SelectTrigger>
                <SelectContent>
                  {entityRelationshipLabels.map((label) => (
                    <SelectItem key={label} value={label}>
                      {t(entityRelationshipLabelKeys[label])}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {draft.label === "custom" ? (
              <div className="space-y-2">
                <Label htmlFor="entity-relationship-custom-label">
                  {t("entities.relationships.fields.customLabel")}
                </Label>
                <Input
                  id="entity-relationship-custom-label"
                  onChange={(event) =>
                    setDraft((current) => ({
                      ...current,
                      customLabel: event.target.value,
                    }))
                  }
                  placeholder={t("entities.relationships.customLabelPlaceholder")}
                  ref={customLabelRef}
                  value={draft.customLabel}
                />
              </div>
            ) : null}

            <div className="space-y-2">
              <Label htmlFor="entity-relationship-target-kind">
                {t("entities.relationships.fields.targetType")}
              </Label>
              <Select
                onValueChange={(value) =>
                  setDraft((current) => ({
                    ...current,
                    targetId:
                      current.targetKind === value ? current.targetId : "",
                    targetKind: value as EntityRelationshipTargetKind,
                  }))
                }
                value={draft.targetKind}
              >
                <SelectTrigger
                  aria-label={t("entities.relationships.fields.targetType")}
                  id="entity-relationship-target-kind"
                >
                  <SelectValue placeholder={t("entities.relationships.targetTypePlaceholder")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="entity-card">
                    {t(entityRelationshipTargetKindLabelKeys["entity-card"])}
                  </SelectItem>
                  <SelectItem value="lore-entry">
                    {t(entityRelationshipTargetKindLabelKeys["lore-entry"])}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="entity-relationship-target">
                {t("entities.relationships.fields.target")}
              </Label>
              <Select
                onValueChange={(value) =>
                  setDraft((current) => ({
                    ...current,
                    targetId: value.startsWith(BROKEN_PREFIX)
                      ? value.slice(BROKEN_PREFIX.length)
                      : value,
                  }))
                }
                value={targetValue}
              >
                <SelectTrigger
                  aria-label={t("entities.relationships.fields.target")}
                  id="entity-relationship-target"
                >
                  <SelectValue
                    placeholder={t("entities.relationships.targetPlaceholder", {
                      targetKind: targetKindLabel,
                    })}
                  />
                </SelectTrigger>
                <SelectContent>
                  {hasBrokenTarget ? (
                    <SelectItem disabled value={targetValue}>
                      {t("entities.relationships.missingTargetOption", {
                        targetKind: targetKindLabel,
                      })}
                    </SelectItem>
                  ) : null}
                  {currentTargetOptions.length ? (
                    currentTargetOptions.map((option) => (
                      <SelectItem key={`${option.kind}:${option.id}`} value={option.id}>
                        {option.title} ({option.description})
                      </SelectItem>
                    ))
                  ) : (
                    <SelectItem disabled value="__none__">
                      {t("entities.relationships.noTargets", {
                        targetKind: targetKindLabel,
                      })}
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>
              {hasBrokenTarget ? (
                <p className="inline-flex items-start gap-2 text-xs text-destructive">
                  <TriangleAlert className="mt-0.5 h-3.5 w-3.5" />
                  {t("entities.relationships.brokenTargetNotice", {
                    targetId: draft.targetId,
                    targetKind: targetKindLabel,
                  })}
                </p>
              ) : null}
            </div>
          </div>

          <div className="flex flex-wrap justify-between gap-3">
            <Button onClick={resetComposer} type="button" variant="ghost">
              {t("common.cancel")}
            </Button>
            <Button
              disabled={!canSave}
              onClick={() => void handleSaveRelationship()}
              type="button"
            >
              {saving ? t("common.saving") : t("entities.relationships.save")}
            </Button>
          </div>
        </div>
      ) : null}

      <div className="space-y-3">
        {card.relationships.length ? (
          card.relationships.map((relationship) => {
            const isEditing = editingRelationshipId === relationship.id;
            const relationshipLabel = getRelationshipLabelText(relationship, t);

            return (
              <div
                key={relationship.id}
                className="rounded-[24px] border border-border/70 bg-white/75 px-4 py-4"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="space-y-2">
                    <p className="text-sm font-semibold text-foreground">
                      {relationshipLabel}
                    </p>
                    {relationship.href && !relationship.broken ? (
                      <Link
                        className="text-sm text-primary underline-offset-4 hover:underline"
                        to={relationship.href}
                      >
                        {relationship.targetTitle}
                      </Link>
                    ) : (
                      <p className="text-sm text-muted-foreground">{relationship.targetTitle}</p>
                    )}
                    <div className="flex flex-wrap gap-2">
                      <Badge variant={relationship.broken ? "outline" : "default"}>
                        {getTargetKindLabel(relationship.targetKind, t)}
                      </Badge>
                      {relationship.targetStatusLabel ? (
                        <Badge variant="outline">{relationship.targetStatusLabel}</Badge>
                      ) : null}
                    </div>
                    {relationship.broken ? (
                      <p className="inline-flex items-center gap-1 text-xs text-destructive">
                        <TriangleAlert className="h-3.5 w-3.5" />
                        {t("entities.relationships.brokenTargetSaved")}
                      </p>
                    ) : null}
                  </div>
                  <div className="flex flex-wrap items-center justify-end gap-1">
                    {relationship.broken ? (
                      <Button
                        onClick={() => startEditRelationship(relationship)}
                        size="sm"
                        type="button"
                        variant="outline"
                      >
                        {t("entities.relationships.repairAction")}
                      </Button>
                    ) : null}
                    <Button
                      onClick={() => startEditRelationship(relationship)}
                      size="icon"
                      type="button"
                      variant="ghost"
                    >
                      <Pencil className="h-4 w-4" />
                      <span className="sr-only">
                        {t("entities.relationships.editAria", {
                          label: relationshipLabel,
                          target: relationship.targetTitle,
                        })}
                      </span>
                    </Button>
                    <Button
                      onClick={() =>
                        void handleDeleteRelationship(relationship.id, isEditing)
                      }
                      size="icon"
                      type="button"
                      variant="ghost"
                    >
                      <X className="h-4 w-4" />
                      <span className="sr-only">
                        {t("entities.relationships.removeAria", {
                          label: relationshipLabel,
                          target: relationship.targetTitle,
                        })}
                      </span>
                    </Button>
                  </div>
                </div>
              </div>
            );
          })
        ) : (
          <div className="rounded-[24px] border border-dashed border-border bg-white/65 px-4 py-5 text-sm text-muted-foreground">
            {t("entities.relationships.empty")}
          </div>
        )}
      </div>
    </section>
  );
}
